import React, { useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Post, v1Patch } from '@/lib/api';
import {
  validateMasterCode,
  validateMasterDisplayName,
  validateMasterCodeUnique,
  validateMasterNameUnique,
  masterDuplicateConflictField,
} from '@/lib/masterDataValidation';
import { useIdSelection } from '@/hooks/useIdSelection';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/StatusPill';
import { Plus, ListTree, Pencil, Trash2, Tags } from 'lucide-react';
import { toast } from 'sonner';

const errDetail = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const BLANK_DEPT_FORM = { code: '', name: '', status: 'active' };
const BLANK_DEPT_FIELD_ERRORS = { code: '', name: '' };

function isDeptFormDirty(form) {
  return !!form.code.trim() || !!form.name.trim() || form.status !== BLANK_DEPT_FORM.status;
}

export const DepartmentsSection = forwardRef(function DepartmentsSection(
  { depts, isLoading, isError, onRequestDelete, onManageCategories },
  ref,
) {
  const qc = useQueryClient();
  const deptSel = useIdSelection();

  const [deptOpen, setDeptOpen] = useState(false);
  const [deptForm, setDeptForm] = useState({ ...BLANK_DEPT_FORM });
  const [deptFieldErrors, setDeptFieldErrors] = useState({ ...BLANK_DEPT_FIELD_ERRORS });
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardConfirmedRef = useRef(false);
  const [editDept, setEditDept] = useState(null);
  const [editDeptForm, setEditDeptForm] = useState({ name: '', status: 'active' });
  const [editDeptNameError, setEditDeptNameError] = useState('');
  const [subOpen, setSubOpen] = useState(null);
  const [subForm, setSubForm] = useState({ code: '', name: '' });
  const [submitting, setSubmitting] = useState(false);

  useImperativeHandle(ref, () => ({
    clearSelection: () => deptSel.clear(),
    closeSubIfDeleted: (ids) => {
      if (subOpen && ids.includes(subOpen.id)) setSubOpen(null);
    },
  }), [deptSel, subOpen]);

  const deptIds = useMemo(() => depts.map((d) => d.id), [depts]);
  const allDeptsSelected = deptIds.length > 0 && deptIds.every((id) => deptSel.selected.has(id));

  const resetDeptForm = () => {
    setDeptForm({ ...BLANK_DEPT_FORM });
    setDeptFieldErrors({ ...BLANK_DEPT_FIELD_ERRORS });
  };

  const applyMasterConflict = (e, fallback, setFieldErrors) => {
    const raw = e?.response?.data?.detail;
    const field = masterDuplicateConflictField(raw);
    if (field) {
      setFieldErrors((prev) => ({ ...prev, [field]: raw }));
      return;
    }
    toast.error(errDetail(e, fallback));
  };

  const handleDeptDialogOpenChange = (open) => {
    if (!open) {
      if (isDeptFormDirty(deptForm)) {
        setDeptOpen(false);
        setDiscardConfirmOpen(true);
        return;
      }
      resetDeptForm();
    }
    setDeptOpen(open);
  };

  const handleDiscardConfirmOpenChange = (open) => {
    setDiscardConfirmOpen(open);
    if (!open) {
      if (discardConfirmedRef.current) {
        discardConfirmedRef.current = false;
        return;
      }
      if (isDeptFormDirty(deptForm)) {
        setDeptOpen(true);
      }
    }
  };

  const confirmDiscardDeptForm = () => {
    discardConfirmedRef.current = true;
    resetDeptForm();
    setDeptOpen(false);
    setDiscardConfirmOpen(false);
  };

  const createDept = async () => {
    setDeptFieldErrors({ ...BLANK_DEPT_FIELD_ERRORS });
    const codeCheck = validateMasterCode(deptForm.code);
    if (!codeCheck.ok) return toast.error(codeCheck.message);
    const codeUnique = validateMasterCodeUnique(codeCheck.value, depts, { entityLabel: 'Department' });
    if (!codeUnique.ok) return setDeptFieldErrors({ code: codeUnique.message, name: '' });
    const nameCheck = validateMasterDisplayName(deptForm.name);
    if (!nameCheck.ok) return toast.error(nameCheck.message);
    const nameUnique = validateMasterNameUnique(nameCheck.value, depts, { entityLabel: 'Department' });
    if (!nameUnique.ok) return setDeptFieldErrors({ code: '', name: nameUnique.message });
    const payload = { ...deptForm, code: codeCheck.value, name: nameCheck.value };
    setSubmitting(true);
    try {
      const created = await v1Post('/master/departments', payload);
      qc.invalidateQueries({ queryKey: ['v1', 'departments'] });
      qc.invalidateQueries({ queryKey: ['v1-master-departments'] });
      if ((created.work_category_count || 0) === 0) {
        toast.success(`Department ${payload.name} created. Add evaluation categories for Tech Assessment and Panel banks.`, {
          duration: 6000,
        });
      } else {
        toast.success(`Department ${payload.name} created with ${created.work_category_count} evaluation categories.`);
      }
      setDeptOpen(false);
      resetDeptForm();
    } catch (e) {
      applyMasterConflict(e, 'Could not create department.', setDeptFieldErrors);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDept = (d) => {
    setEditDept(d);
    setEditDeptForm({ name: d.name, status: d.status });
    setEditDeptNameError('');
  };

  const saveDept = async () => {
    if (!editDept) return;
    setEditDeptNameError('');
    const nameCheck = validateMasterDisplayName(editDeptForm.name);
    if (!nameCheck.ok) return toast.error(nameCheck.message);
    const nameUnique = validateMasterNameUnique(nameCheck.value, depts, {
      excludeId: editDept.id,
      entityLabel: 'Department',
    });
    if (!nameUnique.ok) return setEditDeptNameError(nameUnique.message);
    const payload = { ...editDeptForm, name: nameCheck.value };
    setSubmitting(true);
    try {
      await v1Patch(`/master/departments/${editDept.id}`, payload);
      qc.invalidateQueries({ queryKey: ['v1', 'departments'] });
      toast.success(`Department ${payload.name} updated.`);
      setEditDept(null);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      if (masterDuplicateConflictField(raw) === 'name') setEditDeptNameError(raw);
      else toast.error(errDetail(e, 'Could not update department.'));
    } finally {
      setSubmitting(false);
    }
  };

  const addSubDept = async () => {
    if (!subOpen) return;
    if (!subForm.code || !subForm.name) return toast.error('Code and name are required');
    setSubmitting(true);
    try {
      await v1Post(`/master/departments/${subOpen.id}/sub-departments`, subForm);
      qc.invalidateQueries({ queryKey: ['v1', 'departments'] });
      toast.success(`Sub-department ${subForm.name} added to ${subOpen.name}.`);
      setSubForm({ code: '', name: '' });
      const refreshed = await v1Get('/master/departments');
      const next = (refreshed || []).find((d) => d.id === subOpen.id);
      if (next) setSubOpen(next);
    } catch (e) {
      toast.error(errDetail(e, 'Could not add sub-department.'));
    } finally {
      setSubmitting(false);
    }
  };

  const askDeleteDept = (d) => {
    onRequestDelete({
      kind: 'dept',
      ids: [d.id],
      label: `Delete department “${d.name}”? It will be removed from master lists but kept in the database.`,
    });
  };

  const askBulkDeleteDepts = () => {
    const ids = deptIds.filter((id) => deptSel.selected.has(id));
    if (ids.length === 0) return;
    onRequestDelete({
      kind: 'dept',
      ids,
      label: ids.length === 1
        ? 'Delete 1 selected department? It will be removed from master lists but kept in the database.'
        : `Delete ${ids.length} selected departments? They will be removed from master lists but kept in the database.`,
    });
  };

  return (
    <>
      <Card className="card-elevated flex h-full min-h-0 flex-col overflow-hidden">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b p-4">
          <div>
            <p className="text-sm font-semibold">Departments</p>
            <p className="text-xs text-muted-foreground">
              {isLoading ? 'Loading…' : `${depts.length} departments · click to manage sub-departments`}
              {deptSel.count > 0 ? ` · ${deptSel.count} selected` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {deptSel.count > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1.5 text-rose-700 hover:bg-rose-50"
                onClick={askBulkDeleteDepts}
                data-testid="depts-delete-selected"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete All Selected ({deptSel.count})
              </Button>
            )}
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setDeptOpen(true)} data-testid="add-dept-button">
              <Plus className="h-4 w-4" /> Add department
            </Button>
          </div>
        </div>
        {isError ? (
          <p className="p-4 text-sm text-destructive" data-testid="depts-error">Could not load departments.</p>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="sticky top-0 z-10 bg-secondary/40">
                <TableHead className="w-10">
                  <Checkbox
                    checked={allDeptsSelected}
                    onCheckedChange={(v) => deptSel.setAll(deptIds, v === true)}
                    disabled={deptIds.length === 0}
                    aria-label="Select all departments"
                    data-testid="depts-select-all"
                  />
                </TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {depts.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    No departments yet. Add a department to get started.
                  </TableCell>
                </TableRow>
              ) : (
                depts.map((d) => (
                  <TableRow key={d.id} data-testid="dept-row">
                    <TableCell>
                      <Checkbox
                        checked={deptSel.selected.has(d.id)}
                        onCheckedChange={(v) => deptSel.toggle(d.id, v === true)}
                        aria-label={`Select department ${d.name}`}
                        data-testid={`dept-select-${d.code}`}
                      />
                    </TableCell>
                    <TableCell><code className="rounded bg-secondary px-1.5 py-0.5 text-xs">{d.code}</code></TableCell>
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell><StatusPill status={d.status} size="sm" /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEditDept(d)}
                          aria-label={`Edit department ${d.name}`}
                          data-testid="edit-dept-button"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-7 w-7 ${(d.work_category_count || 0) === 0 ? 'text-amber-700 hover:bg-amber-50' : ''}`}
                          onClick={() => onManageCategories(d)}
                          aria-label={`Manage evaluation categories for ${d.name} (${d.work_category_count ?? 0})`}
                          title={`Categories (${d.work_category_count ?? 0})`}
                          data-testid={`manage-categories-button-${d.code}`}
                        >
                          <Tags className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => { setSubOpen(d); setSubForm({ code: '', name: '' }); }}
                          aria-label={`Manage sub-departments for ${d.name} (${(d.sub_departments || []).length})`}
                          title={`Sub-departments (${(d.sub_departments || []).length})`}
                          data-testid={`manage-subdept-button-${d.code}`}
                        >
                          <ListTree className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-rose-700 hover:bg-rose-50"
                          onClick={() => askDeleteDept(d)}
                          aria-label={`Delete department ${d.name}`}
                          data-testid="delete-dept-button"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        )}
      </Card>

      <Dialog open={deptOpen} onOpenChange={handleDeptDialogOpenChange}>
        <DialogContent data-testid="add-dept-dialog">
          <DialogHeader>
            <DialogTitle>Add new department</DialogTitle>
            <DialogDescription>Departments group candidates and roles. Sub-departments can be added after creation.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Department code</label>
              <Input
                value={deptForm.code}
                onChange={(e) => {
                  setDeptForm({ ...deptForm, code: e.target.value });
                  if (deptFieldErrors.code) setDeptFieldErrors({ ...deptFieldErrors, code: '' });
                }}
                placeholder="e.g. Ops"
                aria-invalid={!!deptFieldErrors.code}
                data-testid="dept-code-input"
              />
              {deptFieldErrors.code ? (
                <p className="mt-1 text-xs text-destructive" data-testid="dept-code-error">{deptFieldErrors.code}</p>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Display name</label>
              <Input
                value={deptForm.name}
                onChange={(e) => {
                  setDeptForm({ ...deptForm, name: e.target.value });
                  if (deptFieldErrors.name) setDeptFieldErrors({ ...deptFieldErrors, name: '' });
                }}
                placeholder="e.g. Operations"
                aria-invalid={!!deptFieldErrors.name}
                data-testid="dept-name-input"
              />
              {deptFieldErrors.name ? (
                <p className="mt-1 text-xs text-destructive" data-testid="dept-name-error">{deptFieldErrors.name}</p>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={deptForm.status} onValueChange={(v) => setDeptForm({ ...deptForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => handleDeptDialogOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={createDept} disabled={submitting} data-testid="dept-create-confirm">{submitting ? 'Creating…' : 'Create department'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={handleDiscardConfirmOpenChange}>
        <AlertDialogContent data-testid="add-dept-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes. Are you sure you want to discard them?</AlertDialogTitle>
            <AlertDialogDescription>
              Your entered department details will be lost if you discard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="add-dept-continue-editing">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardDeptForm} data-testid="add-dept-discard-confirm">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editDept} onOpenChange={(v) => !v && setEditDept(null)}>
        <DialogContent data-testid="edit-dept-dialog">
          <DialogHeader>
            <DialogTitle>Edit department</DialogTitle>
            <DialogDescription>Update the display name or status. The department code is immutable once created.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Department code</label>
              <Input value={editDept?.code || ''} disabled data-testid="edit-dept-code-input" />
              <p className="mt-1 text-[11px] text-muted-foreground">The code is locked because it is referenced by candidates and pipelines.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Display name</label>
              <Input
                value={editDeptForm.name}
                onChange={(e) => {
                  setEditDeptForm({ ...editDeptForm, name: e.target.value });
                  if (editDeptNameError) setEditDeptNameError('');
                }}
                placeholder="e.g. Operations"
                aria-invalid={!!editDeptNameError}
                data-testid="edit-dept-name-input"
              />
              {editDeptNameError ? (
                <p className="mt-1 text-xs text-destructive" data-testid="edit-dept-name-error">{editDeptNameError}</p>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={editDeptForm.status} onValueChange={(v) => setEditDeptForm({ ...editDeptForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDept(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={saveDept} disabled={submitting} data-testid="dept-edit-confirm">{submitting ? 'Saving…' : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!subOpen} onOpenChange={(v) => !v && setSubOpen(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="manage-subdept-dialog">
          <DialogHeader>
            <DialogTitle>{subOpen ? `Sub-departments · ${subOpen.name}` : 'Sub-departments'}</DialogTitle>
            <DialogDescription>Sub-departments help refine candidate role placement (e.g. Backend / Frontend under Engineering).</DialogDescription>
          </DialogHeader>
          {subOpen && (
            <div className="space-y-4">
              <div className="rounded-xl border bg-secondary/30 p-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Existing</p>
                {(subOpen.sub_departments || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No sub-departments yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {(subOpen.sub_departments || []).map((s) => (
                      <span key={s.code} className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs">
                        <code className="text-[10px] text-muted-foreground">{s.code}</code>
                        <span className="font-medium">{s.name}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Sub-dept code</label>
                  <Input value={subForm.code} onChange={(e) => setSubForm({ ...subForm, code: e.target.value })} placeholder="e.g. backend" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Display name</label>
                  <Input value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} placeholder="e.g. Backend" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubOpen(null)} disabled={submitting}>Close</Button>
            <Button onClick={addSubDept} disabled={submitting} data-testid="add-subdept-confirm">{submitting ? 'Adding…' : 'Add sub-department'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});
